import React, { useState, useEffect, useCallback } from 'react';
import { Button, Box, Typography, Collapse } from '@mui/material';
import { ICategory } from '../types/damTypes';
import axios from 'axios';
import { API_BASE_URL } from '../config';

interface CategoryFilterProps {
  categories: ICategory[];
  selectedCategoryIds: string[];
  onCategoryToggle: (categoryId: string) => void;
  onCategorySelect: (categoryIds: string[]) => void;
  align?: 'center' | 'start';
}

const formatCategoryButtonLabel = (label: string): string => {
  const trimmed = label.trim();
  if (!trimmed) return label;
  const lower = trimmed.toLocaleLowerCase('sv-SE');
  return lower.charAt(0).toLocaleUpperCase('sv-SE') + lower.slice(1);
};

const CategoryFilter: React.FC<CategoryFilterProps> = ({
  categories,
  selectedCategoryIds,
  onCategoryToggle,
  onCategorySelect,
  align = 'center',
}) => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  
  // Filter main categories (those without a parent)
  const mainCategories = categories.filter(cat => !cat.parentCategory);
  
  // Get subcategories for a specific parent category from the existing categories data
  const getSubcategories = useCallback((parentId: string) => {
    const subs = categories.filter(cat => {
      // Handle parentCategory being a string, object, or null
      if (typeof cat.parentCategory === 'string') {
        return cat.parentCategory === parentId;
      } else if (cat.parentCategory && typeof cat.parentCategory === 'object') {
        return (cat.parentCategory as {_id: string})._id === parentId;
      }
      return false;
    });
    
    console.log(`Found ${subs.length} subcategories for parent ${parentId}:`, subs);
    return subs;
  }, [categories]);
  
  // Hitta huvudkategorin för en underkategori
  const getParentCategoryId = useCallback((subcategoryId: string): string | null => {
    const subcategory = categories.find(cat => cat._id === subcategoryId);
    if (!subcategory || !subcategory.parentCategory) return null;
    
    if (typeof subcategory.parentCategory === 'string') {
      return subcategory.parentCategory;
    } else if (subcategory.parentCategory && typeof subcategory.parentCategory === 'object') {
      return (subcategory.parentCategory as {_id: string})._id;
    }
    
    return null;
  }, [categories]);
  
  // Se om någon av de valda kategorierna är underkategorier till en specifik huvudkategori
  const hasSelectedSubcategories = useCallback((parentCategoryId: string, selection: string[] = selectedCategoryIds): boolean => {
    const subcategories = getSubcategories(parentCategoryId);
    return subcategories.some(subcat => selection.includes(subcat._id));
  }, [getSubcategories, selectedCategoryIds]);
  
  // Handle main category click - bara tillåt val av en huvudkategori i taget
  const handleMainCategoryClick = useCallback((categoryId: string) => {
    const isCurrentlySelected = selectedCategoryIds.includes(categoryId);
    
    if (isCurrentlySelected) {
      // Om kategori redan är vald, ta bort den och alla dess underkategorier
      const subcategories = getSubcategories(categoryId);
      const subcategoryIds = subcategories.map(subcat => subcat._id);
      const newSelectedCategories = selectedCategoryIds.filter(
        id => id !== categoryId && !subcategoryIds.includes(id)
      );
      onCategorySelect(newSelectedCategories);
      setExpandedCategory(null); // Stäng expanderad vy
    } else {
      // Om kategorin inte är vald, välj endast denna huvudkategori
      // Ta bort alla andra huvudkategorier och deras underkategorier
      const newSelectedCategories: string[] = [categoryId];
      setExpandedCategory(categoryId); // Expandera den valda kategorin
      onCategorySelect(newSelectedCategories);
    }
  }, [selectedCategoryIds, getSubcategories, onCategorySelect]);

  // Handle subcategory click - tillåt flera underkategorier
  const handleSubcategoryClick = useCallback((subcategoryId: string) => {
    const isSubcategorySelected = selectedCategoryIds.includes(subcategoryId);
    const parentCategoryId = getParentCategoryId(subcategoryId);
    
    if (isSubcategorySelected) {
      // Om underkategori är vald, ta bort den
      const newSelectedCategories = selectedCategoryIds.filter(id => id !== subcategoryId);
      
      // Kontrollera om det fortfarande finns valda underkategorier
      if (parentCategoryId && !hasSelectedSubcategories(parentCategoryId, newSelectedCategories)) {
        // Om inga valda underkategorier kvar, lägg till huvudkategorin igen
        if (!newSelectedCategories.includes(parentCategoryId)) {
          newSelectedCategories.push(parentCategoryId);
        }
      }
      
      onCategorySelect(newSelectedCategories);
    } else {
      // Om underkategori inte är vald, lägg till den
      const newSelectedCategories = [...selectedCategoryIds];
      
      // Om huvudkategorin är vald, ta bort den
      if (parentCategoryId && newSelectedCategories.includes(parentCategoryId)) {
        const parentIndex = newSelectedCategories.indexOf(parentCategoryId);
        newSelectedCategories.splice(parentIndex, 1);
      }
      
      // Lägg till underkategorin
      newSelectedCategories.push(subcategoryId);
      onCategorySelect(newSelectedCategories);
    }
  }, [selectedCategoryIds, getParentCategoryId, hasSelectedSubcategories, onCategorySelect]);
  
  // Effect för att säkerställa att rätt huvudkategori är expanderad
  useEffect(() => {
    // Kontrollera om någon vald kategori är en underkategori
    const selectedSubcategory = selectedCategoryIds.find(id => {
      const parentId = getParentCategoryId(id);
      return parentId !== null;
    });
    
    if (selectedSubcategory) {
      const parentId = getParentCategoryId(selectedSubcategory);
      if (parentId) {
        setExpandedCategory(prev => prev !== parentId ? parentId : prev);
      }
    }
  }, [selectedCategoryIds, getParentCategoryId]);

  // Get subcategories for the currently expanded category
  const currentSubcategories = expandedCategory ? getSubcategories(expandedCategory) : [];

  // Avgör om en huvudkategori är aktiv (antingen vald eller har valda underkategorier)
  const isMainCategoryActive = useCallback((categoryId: string): boolean => {
    return selectedCategoryIds.includes(categoryId) || hasSelectedSubcategories(categoryId);
  }, [selectedCategoryIds, hasSelectedSubcategories]);

  return (
    <Box sx={{ mb: 2 }} className="category-filter">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: align === 'center' ? 'center' : 'stretch' }}>
        {/* Main categories */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
          {mainCategories.map(category => (
            <Button
              key={category._id}
              variant={isMainCategoryActive(category._id) ? 'contained' : 'outlined'}
              size="medium"
              onClick={() => handleMainCategoryClick(category._id)}
              sx={{ 
                borderColor: '#e7e6e6', 
                color: isMainCategoryActive(category._id) ? 'white' : '#333',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#e7e6e6'
                },
                padding: '8px 16px',
                fontSize: '0.95rem',
                backgroundColor: !isMainCategoryActive(category._id)
                  ? '#fff'
                  : hasSelectedSubcategories(category._id) && !selectedCategoryIds.includes(category._id) 
                    ? 'primary.main' 
                    : undefined
              }}
            >
              {formatCategoryButtonLabel(category.name)}
            </Button>
          ))}
          {mainCategories.length === 0 && <Typography variant="body2">Inga huvudkategorier tillgängliga.</Typography>}
        </Box>
        
        {/* Subcategories - shown when a main category is expanded */}
        {expandedCategory && (
          <Collapse in={!!expandedCategory} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              mt: 1,
              pb: 1,
              borderLeft: '1px solid #ccc',
              paddingLeft: 2,
              justifyContent: align === 'center' ? 'center' : 'flex-start'
            }}>
              {currentSubcategories.length > 0 ? (
                currentSubcategories.map(subcat => (
                  <Button
                    key={subcat._id}
                    variant={selectedCategoryIds.includes(subcat._id) ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => handleSubcategoryClick(subcat._id)}
                    sx={{ 
                      borderColor: '#e7e6e6', 
                      color: selectedCategoryIds.includes(subcat._id) ? 'white' : '#333',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#e7e6e6'
                      },
                      padding: '6px 12px',
                      fontSize: '0.85rem'
                    }}
                  >
                    {formatCategoryButtonLabel(subcat.name)}
                  </Button>
                ))
              ) : (
                <Typography variant="body2">Inga underkategorier tillgängliga.</Typography>
              )}
            </Box>
          </Collapse>
        )}
      </Box>
    </Box>
  );
};

export default CategoryFilter; 